<template>
  <div v-if="avisoVisivel" class="alert tour-guiado-aviso" role="alert">
    <p class="tour-guiado-aviso__texto">{{ avisoTexto }}</p>
    <button type="button" class="btn btn-nd-primary tour-guiado-aviso__acao" @click="fecharAviso">
      Entendi
    </button>
  </div>
  <span v-else class="d-none" aria-hidden="true"></span>
</template>

<script setup>
import { getCurrentInstance, onMounted, onUnmounted, ref } from 'vue'
import { indiceRetomavel, useTourPlayer } from '../composables/useTourPlayer.js'
import { criarRegistroDeInterrupcoes } from '../composables/useRegistroDeInterrupcoes.js'
import {
  ACOES_DO_TOUR,
  MOTIVOS_DE_INTERRUPCAO,
  montarDadosDaInterrupcao
} from '../utils/motivos_de_interrupcao.js'
import { limparRotasSemTour, registrarRotaSemTour, rotaSemTour } from '../utils/rotas_sem_tour.js'
import './TourOptInModal.css'

const props = defineProps({
  identificacao: {
    type: Object,
    default: () => ({ pessoa_id: null, empresa_id: null })
  }
})

const registroDeInterrupcoes = criarRegistroDeInterrupcoes({
  capturar: getCurrentInstance().appContext.config.globalProperties.$posthogCapture,
  identificacao: props.identificacao
})

const MAX_TENTATIVAS_NAVEGACAO = 2
const CHAVE_AVISO_NAVEGACAO_SEM_SUCESSO = 'tour_guiado.player.avisos.navegacao_sem_sucesso'
const CHAVE_AVISO_ROTA_INALCANCAVEL = 'tour_guiado.player.avisos.rota_inalcancavel'
const CHAVE_AVISO_ALVO_INALCANCAVEL = 'tour_guiado.player.avisos.alvo_inalcancavel'

const avisoVisivel = ref(false)
const avisoTexto = ref('')

function exibirAviso(chave) {
  avisoTexto.value = window.t(chave)
  avisoVisivel.value = true
}

function exibirAvisoRotaInalcancavel() {
  exibirAviso(CHAVE_AVISO_ROTA_INALCANCAVEL)
}

function exibirAvisoAlvoInalcancavel() {
  exibirAviso(CHAVE_AVISO_ALVO_INALCANCAVEL)
}

const {
  executarTour,
  retomarTour,
  carregarEstado,
  limparEstado,
  marcarConclusao,
  tentativasDeNavegacao,
  registrarTentativaNavegacao,
  rotaInternaValida,
  rotaContemCuringa,
  builderAtivo,
  encerrarSilenciosamente
} = useTourPlayer({
  aoRotaInalcancavel: exibirAvisoRotaInalcancavel,
  aoAlvoInalcancavel: exibirAvisoAlvoInalcancavel,
  aoInterromper: registroDeInterrupcoes.registrar
})

function registrarInterrupcaoNaRetomada(motivo, estado) {
  const tour = {
    id: estado.tour_id,
    nome: estado.nome,
    passos: estado.passos,
    rota_pattern: estado.rota_pattern
  }
  const dados = montarDadosDaInterrupcao({
    tour,
    indicePasso: estado.indice_passo,
    acao: ACOES_DO_TOUR.RETOMADA
  })
  registroDeInterrupcoes.registrar(motivo, dados)
}

function aoAtivarBuilder() {
  limparRotasSemTour()
  encerrarSilenciosamente()
}

function fecharAviso() {
  avisoVisivel.value = false
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

function tourOfertado(data) {
  return Boolean(data && data.passos && data.passos.length > 0)
}

function aoResponderTourPendente(rota, data) {
  if (!tourOfertado(data)) {
    registrarRotaSemTour(rota)
    return
  }
  if (builderAtivo()) return

  executarTour(data)
}

function consultarTourPendente(rota) {
  return fetch(`/tours_guiados/pendente?rota=${encodeURIComponent(rota)}`, {
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
  })
    .then((res) => {
      if (!res.ok) return
      return res.json().then((data) => aoResponderTourPendente(rota, data))
    })
    .catch((e) => {
      console.error('Falha ao consultar tour pendente:', e)
    })
}

function rotaDoPassoSalvo(estado) {
  const passo = estado.passos?.[estado.indice_passo]
  return passo?.rota_pattern || estado.rota_pattern
}

async function encerrarTourSalvo(estado, chaveAviso) {
  exibirAviso(chaveAviso)
  if (estado.tour_id) await marcarConclusao(estado.tour_id, true)
  limparEstado()
}

async function tentarRetomada() {
  const estado = carregarEstado()
  if (!estado) return false

  if (!Array.isArray(estado.passos) || estado.passos.length === 0) {
    limparEstado()
    return false
  }

  const indiceCasado = indiceRetomavel(estado)

  if (indiceCasado !== null) {
    retomarTour({ ...estado, indice_passo: indiceCasado })
    return true
  }

  const rotaEsperada = rotaDoPassoSalvo(estado)

  if (!rotaInternaValida(rotaEsperada)) {
    registrarInterrupcaoNaRetomada(MOTIVOS_DE_INTERRUPCAO.ROTA_INVALIDA, estado)
    limparEstado()
    return false
  }

  if (!estado.navegacao_pelo_tour) return false

  if (rotaContemCuringa(rotaEsperada)) {
    registrarInterrupcaoNaRetomada(MOTIVOS_DE_INTERRUPCAO.ROTA_COM_CURINGA, estado)
    await encerrarTourSalvo(estado, CHAVE_AVISO_ROTA_INALCANCAVEL)
    return false
  }

  if (tentativasDeNavegacao(estado) >= MAX_TENTATIVAS_NAVEGACAO) {
    registrarInterrupcaoNaRetomada(MOTIVOS_DE_INTERRUPCAO.NAVEGACAO_SEM_SUCESSO, estado)
    await encerrarTourSalvo(estado, CHAVE_AVISO_NAVEGACAO_SEM_SUCESSO)
    return false
  }

  registrarTentativaNavegacao(estado)
  window.location.assign(rotaEsperada)
  return true
}

onMounted(async () => {
  window.addEventListener('tour-builder:ativado', aoAtivarBuilder)

  if (builderAtivo()) return
  if (await tentarRetomada()) return

  const rota = window.location.pathname
  if (rotaSemTour(rota)) return

  return consultarTourPendente(rota)
})

onUnmounted(() => {
  window.removeEventListener('tour-builder:ativado', aoAtivarBuilder)
})
</script>
